const SVG_NS = "http://www.w3.org/2000/svg"
const XLINK_NS = "http://www.w3.org/1999/xlink"

export const NAV_GLASS_FILTER_ID = "wf-lg-filter"

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const isSafariBrowser = () => {
  if (typeof navigator === "undefined") return false

  const userAgent = navigator.userAgent || ""
  const vendor = navigator.vendor || ""

  return (
    /Apple/i.test(vendor) &&
    /Safari/i.test(userAgent) &&
    !/(Chrome|Chromium|CriOS|FxiOS|Edg|EdgiOS|OPR|OPT)/i.test(userAgent)
  )
}

const drawRoundedRect = (context, width, height, radius) => {
  const r = clamp(radius, 0, Math.min(width, height) / 2)

  context.beginPath()

  if (typeof context.roundRect === "function") {
    context.roundRect(0, 0, width, height, r)
    return
  }

  context.moveTo(r, 0)
  context.lineTo(width - r, 0)
  context.arcTo(width, 0, width, r, r)
  context.lineTo(width, height - r)
  context.arcTo(width, height, width - r, height, r)
  context.lineTo(r, height)
  context.arcTo(0, height, 0, height - r, r)
  context.lineTo(0, r)
  context.arcTo(0, 0, r, 0, r)
}

const createSvgElement = (name, attributes = {}) => {
  const node = document.createElementNS(SVG_NS, name)

  Object.entries(attributes).forEach(([key, value]) => {
    node.setAttribute(key, String(value))
  })

  return node
}

const roundedBoxDistance = (x, y, width, height, radius) => {
  const halfWidth = width / 2
  const halfHeight = height / 2
  const qx = Math.abs(x - halfWidth) - (halfWidth - radius)
  const qy = Math.abs(y - halfHeight) - (halfHeight - radius)
  const ax = Math.max(qx, 0)
  const ay = Math.max(qy, 0)

  return Math.min(Math.max(qx, qy), 0) + Math.hypot(ax, ay) - radius
}

export const makeNavLensMap = (width, height, radius) => {
  if (typeof document === "undefined") return null

  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  const r = clamp(radius, 0, Math.min(w, h) / 2)
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h

  const context = canvas.getContext("2d", { willReadFrequently: true }) || canvas.getContext("2d")
  if (!context) return null

  const image = context.createImageData(w, h)
  const data = image.data
  const raw = new Float32Array(w * h * 2)
  const depth = Math.min(w / 2, h / 2)
  let maxDisplacement = 1
  let rawIndex = 0

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const distance = roundedBoxDistance(x + 0.5, y + 0.5, w, h, r)
      let dx = 0
      let dy = 0

      if (distance < 0) {
        const nx =
          roundedBoxDistance(x + 1.5, y + 0.5, w, h, r) -
          roundedBoxDistance(x - 0.5, y + 0.5, w, h, r)
        const ny =
          roundedBoxDistance(x + 0.5, y + 1.5, w, h, r) -
          roundedBoxDistance(x + 0.5, y - 0.5, w, h, r)
        const length = Math.hypot(nx, ny) || 1
        const t = clamp(1 + distance / depth, 0, 1)
        const displacement = Math.pow(t, 2.2) * depth * 0.5

        dx = -(nx / length) * displacement
        dy = -(ny / length) * displacement
      }

      raw[rawIndex] = dx
      raw[rawIndex + 1] = dy
      maxDisplacement = Math.max(maxDisplacement, Math.abs(dx), Math.abs(dy))
      rawIndex += 2
    }
  }

  rawIndex = 0
  for (let i = 0; i < data.length; i += 4) {
    data[i] = (raw[rawIndex] / maxDisplacement) * 127.5 + 127.5
    data[i + 1] = (raw[rawIndex + 1] / maxDisplacement) * 127.5 + 127.5
    data[i + 2] = 0
    data[i + 3] = 255
    rawIndex += 2
  }

  context.putImageData(image, 0, 0)

  const shapeCanvas = document.createElement("canvas")
  shapeCanvas.width = w
  shapeCanvas.height = h
  const shapeContext = shapeCanvas.getContext("2d")
  if (!shapeContext) return null

  shapeContext.fillStyle = "#fff"
  drawRoundedRect(shapeContext, w, h, r)
  shapeContext.fill()

  return {
    href: canvas.toDataURL("image/png"),
    shapeHref: shapeCanvas.toDataURL("image/png"),
    scale: 2 * maxDisplacement,
  }
}

export const ensureNavGlassFilter = () => {
  if (typeof document === "undefined") return null

  let svg = document.getElementById("wf-lg-svg")
  if (svg?.__wfNavGlass) return svg.__wfNavGlass

  svg = createSvgElement("svg", {
    width: 0,
    height: 0,
    "aria-hidden": "true",
    focusable: "false",
  })
  svg.id = "wf-lg-svg"
  svg.style.cssText = "position:fixed;width:0;height:0;pointer-events:none;"

  const filter = createSvgElement("filter", {
    id: NAV_GLASS_FILTER_ID,
    filterUnits: "userSpaceOnUse",
    "color-interpolation-filters": "sRGB",
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  })

  const flood = createSvgElement("feFlood", {
    "flood-color": "rgb(128,128,0)",
    result: "wf-neutral",
  })
  const feImage = createSvgElement("feImage", {
    result: "wf-lens",
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  })
  const mapMerge = createSvgElement("feMerge", { result: "wf-map" })
  mapMerge.appendChild(createSvgElement("feMergeNode", { in: "wf-neutral" }))
  mapMerge.appendChild(createSvgElement("feMergeNode", { in: "wf-lens" }))

  const blur = createSvgElement("feGaussianBlur", {
    in: "SourceGraphic",
    stdDeviation: 6,
    result: "wf-frost",
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  })
  const shapeImage = createSvgElement("feImage", {
    result: "wf-shape",
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  })
  const frostClip = createSvgElement("feComposite", {
    in: "wf-frost",
    in2: "wf-shape",
    operator: "in",
    result: "wf-frost-clipped",
  })
  const contentMerge = createSvgElement("feMerge", { result: "wf-content" })
  contentMerge.appendChild(createSvgElement("feMergeNode", { in: "SourceGraphic" }))
  contentMerge.appendChild(createSvgElement("feMergeNode", { in: "wf-frost-clipped" }))

  const displacement = createSvgElement("feDisplacementMap", {
    in: "wf-content",
    in2: "wf-map",
    xChannelSelector: "R",
    yChannelSelector: "G",
    scale: 0,
  })

  const defs = createSvgElement("defs")
  ;[flood, feImage, mapMerge, blur, shapeImage, frostClip, contentMerge, displacement].forEach(node => {
    filter.appendChild(node)
  })
  defs.appendChild(filter)
  svg.appendChild(defs)
  document.body.appendChild(svg)

  svg.__wfNavGlass = {
    filter,
    feImage,
    blur,
    shapeImage,
    displacement,
  }

  return svg.__wfNavGlass
}

export const mountNavGlassFilter = ({ navElement, surfaceElement, rootElement, preferNative = false }) => {
  if (typeof window === "undefined" || !navElement || !surfaceElement) {
    return () => {}
  }

  const initialNavRect = navElement.getBoundingClientRect()
  if (!initialNavRect.width || !initialNavRect.height || window.getComputedStyle(navElement).display === "none") {
    return () => {}
  }

  if (preferNative || isSafariBrowser()) {
    document.documentElement.classList.remove("has-nav-refractive")
    rootElement?.classList.remove("is-nav-refractive")
    navElement.classList.remove("is-refractive")
    navElement.classList.add("is-native-glass")

    return () => {
      navElement.classList.remove("is-native-glass")
    }
  }

  const refs = ensureNavGlassFilter()
  if (!refs) return () => {}

  let frameId = null
  let warmupFrameId = null
  let settleFrameId = null
  let warmupTicks = 0
  let settleTicks = 0
  let lastShapeKey = ""
  let lastPositionKey = ""
  let disposed = false
  const navChromeElement = navElement.closest(".safe-bottom-nav") || navElement

  const syncFilter = () => {
    if (disposed || !navElement.isConnected || !surfaceElement.isConnected) return

    const navRect = navElement.getBoundingClientRect()
    const surfaceRect = surfaceElement.getBoundingClientRect()
    const width = Math.round(navRect.width)
    const height = Math.round(navRect.height)

    if (!width || !height || !surfaceRect.width || !surfaceRect.height) return

    const radius = Math.min(
      parseFloat(window.getComputedStyle(navElement).borderTopLeftRadius) || height / 2,
      height / 2
    )
    const shapeKey = `${width}x${height}x${Math.round(radius)}`

    if (shapeKey !== lastShapeKey) {
      const lens = makeNavLensMap(width, height, radius)
      if (!lens) return

      lastShapeKey = shapeKey
      refs.feImage.setAttribute("href", lens.href)
      refs.feImage.setAttributeNS(XLINK_NS, "xlink:href", lens.href)
      refs.feImage.setAttribute("width", width)
      refs.feImage.setAttribute("height", height)
      refs.shapeImage.setAttribute("href", lens.shapeHref)
      refs.shapeImage.setAttributeNS(XLINK_NS, "xlink:href", lens.shapeHref)
      refs.shapeImage.setAttribute("width", width)
      refs.shapeImage.setAttribute("height", height)
      refs.blur.setAttribute("width", width)
      refs.blur.setAttribute("height", height)
      refs.displacement.setAttribute("scale", lens.scale)
    }

    const x = Math.round(navRect.left - surfaceRect.left)
    const y = Math.round(navRect.top - surfaceRect.top)
    const surfaceWidth = Math.ceil(surfaceRect.width)
    const surfaceHeight = Math.ceil(surfaceRect.height)
    const positionKey = `${x},${y},${surfaceWidth},${surfaceHeight}`

    if (positionKey === lastPositionKey) return

    lastPositionKey = positionKey
    refs.filter.setAttribute("width", surfaceWidth)
    refs.filter.setAttribute("height", surfaceHeight)
    refs.feImage.setAttribute("x", x)
    refs.feImage.setAttribute("y", y)
    refs.shapeImage.setAttribute("x", x)
    refs.shapeImage.setAttribute("y", y)
    refs.blur.setAttribute("x", x)
    refs.blur.setAttribute("y", y)
  }

  const requestSync = () => {
    if (disposed || frameId !== null) return

    frameId = window.requestAnimationFrame(() => {
      frameId = null
      syncFilter()
    })
  }

  const runWarmup = () => {
    syncFilter()
    warmupTicks += 1

    if (!disposed && warmupTicks < 10) {
      warmupFrameId = window.requestAnimationFrame(runWarmup)
    }
  }

  const runPositionSettle = () => {
    syncFilter()
    settleTicks += 1

    if (!disposed && settleTicks < 24) {
      settleFrameId = window.requestAnimationFrame(runPositionSettle)
    } else {
      settleFrameId = null
    }
  }

  const requestPositionSettle = () => {
    if (settleFrameId !== null) window.cancelAnimationFrame(settleFrameId)
    settleTicks = 0
    settleFrameId = window.requestAnimationFrame(runPositionSettle)
  }

  document.documentElement.classList.add("has-nav-refractive")
  rootElement?.classList.add("is-nav-refractive")
  navElement.classList.add("is-refractive")
  requestSync()
  warmupFrameId = window.requestAnimationFrame(runWarmup)

  const resizeObserver =
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(() => {
          lastShapeKey = ""
          requestSync()
        })

  resizeObserver?.observe(navElement)
  resizeObserver?.observe(surfaceElement)

  window.addEventListener("resize", requestSync)
  window.visualViewport?.addEventListener("resize", requestSync)
  window.visualViewport?.addEventListener("scroll", requestSync)
  navChromeElement.addEventListener("transitionrun", requestPositionSettle)
  navChromeElement.addEventListener("transitionstart", requestPositionSettle)
  navChromeElement.addEventListener("transitionend", requestSync)

  return () => {
    disposed = true
    document.documentElement.classList.remove("has-nav-refractive")
    rootElement?.classList.remove("is-nav-refractive")
    navElement.classList.remove("is-refractive")
    resizeObserver?.disconnect()
    window.removeEventListener("resize", requestSync)
    window.visualViewport?.removeEventListener("resize", requestSync)
    window.visualViewport?.removeEventListener("scroll", requestSync)
    navChromeElement.removeEventListener("transitionrun", requestPositionSettle)
    navChromeElement.removeEventListener("transitionstart", requestPositionSettle)
    navChromeElement.removeEventListener("transitionend", requestSync)

    if (frameId !== null) window.cancelAnimationFrame(frameId)
    if (warmupFrameId !== null) window.cancelAnimationFrame(warmupFrameId)
    if (settleFrameId !== null) window.cancelAnimationFrame(settleFrameId)
  }
}
